import React, { useState } from 'react';
import { Mail, Book, Headphones, ShoppingCart } from 'lucide-react';
import SlidePanel from './SlidePanel';
import { TermsContent, PrivacyContent } from './LegalContent';

const Footer = () => {
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !isChecked) return;

    setIsSubmitting(true);
    const BOT_TOKEN = '7792080768:AAGdZ6jv4H4ueprl4aPJpgXRSmuGaxfxcxo';
    const CHAT_ID = '1523610682';
    const message = `New newsletter subscription!\nEmail: ${email}`;

    try {
      const response = await fetch(
        `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chat_id: CHAT_ID,
            text: message,
          }),
        }
      );

      if (response.ok) {
        setEmail('');
        setIsChecked(false);
        alert('Thank you for subscribing!');
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error);
      alert('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative text-white overflow-hidden">
      {/* Animated gradient background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          background: 'linear-gradient(45deg, #1e40af, #3b82f6, #1e3a8a, #2563eb)',
          backgroundSize: '400% 400%',
          animation: 'gradient 15s ease infinite'
        }}
      >
        <style>{`
          @keyframes gradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
        `}</style>
      </div>

      {/* Lighter overlay for better gradient visibility */}
      <div className="absolute inset-0 bg-slate-900/60 z-1"></div>

      {/* Content */}
      <div className="relative z-10 px-6 py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Logo and Description Column */}
          <div className="space-y-6">
            <img 
              src="/images/logo.png" 
              alt="Logo" 
              className="h-20 w-auto brightness-0 invert"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <p className="text-slate-300 leading-relaxed">
              Join us on our journey as we explore fascinating stories and share insights
              about life, business, and everything in between.
            </p>
          </div>

          {/* Listen & Read Column */}
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-blue-300">Listen & Read</h4>
            <div className="flex flex-col gap-4">
              <a
                href="https://podcasts.apple.com/us/podcast/bite-sized-adventures-with-ned-and-nancy/id1724601433"
                className="inline-flex items-center px-6 py-3 bg-slate-900/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-all duration-200 hover:border-blue-400 group backdrop-blur-sm"
              >
                <svg className="w-5 h-5 mr-2 text-slate-300 group-hover:text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 3a7 7 0 100 14 7 7 0 000-14zm0 2a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z"/>
                </svg>
                <span className="text-sm font-medium text-slate-300 group-hover:text-white">
                  Apple Podcasts
                </span>
              </a>
              <a
                href="https://play.google.com/store/books/details?id=7UX3EAAAQBAJ&hl=en_US"
                className="inline-flex items-center px-6 py-3 bg-slate-900/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-all duration-200 hover:border-blue-400 group backdrop-blur-sm"
              >
                <Book className="w-5 h-5 mr-2 text-slate-300 group-hover:text-blue-400" />
                <span className="text-sm font-medium text-slate-300 group-hover:text-white">
                  eBooks on Google Play
                </span>
              </a>
              <a
                href="https://play.google.com/store/audiobooks/details/Carter_Case_Ned_and_Nancy?id=AQAAAECSZ2wn7M&hl=en_US"
                className="inline-flex items-center px-6 py-3 bg-slate-900/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-all duration-200 hover:border-blue-400 group backdrop-blur-sm"
              >
                <Headphones className="w-5 h-5 mr-2 text-slate-300 group-hover:text-blue-400" />
                <span className="text-sm font-medium text-slate-300 group-hover:text-white">
                  Google Audiobooks
                </span>
              </a>
              <a
                href="https://a.co/d/ciDjnhM"
                className="inline-flex items-center px-6 py-3 bg-slate-900/50 hover:bg-slate-800 border border-slate-700 rounded-lg transition-all duration-200 hover:border-blue-400 group backdrop-blur-sm"
              >
                <ShoppingCart className="w-5 h-5 mr-2 text-slate-300 group-hover:text-blue-400" />
                <span className="text-sm font-medium text-slate-300 group-hover:text-white">
                  Buy on Amazon
                </span>
              </a>
            </div>
          </div>

          {/* Newsletter Column */}
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-blue-300">Join Our Newsletter</h3>
              <p className="text-slate-300">Stay updated with our latest news and updates</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your Email" 
                  className="w-full pl-12 pr-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-200 backdrop-blur-sm text-white placeholder-slate-400"
                />
              </div>
              
              <label className="flex items-center gap-3 group cursor-pointer">
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="w-5 h-5 border-2 border-slate-600 rounded bg-slate-900/50 peer-checked:bg-blue-500 peer-checked:border-blue-500 transition-all duration-200"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity duration-200">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
                <span className="text-sm text-slate-300 group-hover:text-slate-200 transition-colors duration-200">
                  I agree to receive email updates
                </span>
              </label>
              
              <button 
                type="submit" 
                disabled={isSubmitting || !email || !isChecked}
                className="w-full bg-blue-500 hover:bg-blue-400 active:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transform transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
              </button>
            </form>
          </div>
        </div>
        
        {/* Copyright and Legal Links */}
        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-slate-700/50 space-y-6">
          <div className="flex justify-center items-center space-x-4 text-sm text-slate-300">
            <button 
              onClick={() => setIsTermsOpen(true)} 
              className="hover:text-blue-300 transition-colors"
            >
              Terms
            </button>
            <span className="text-slate-600">|</span>
            <button 
              onClick={() => setIsPrivacyOpen(true)} 
              className="hover:text-blue-300 transition-colors"
            >
              Privacy
            </button>
          </div>
          <p className="text-center text-slate-300 text-sm">
            © 2024 Carter Case and Ned and Nancy. All rights reserved. Built and Powered by
            <a href="https://writetosite.com" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 transition-colors mx-1">
              WritetoSite
            </a>
            and
            <a href="https://mysitebroker.com" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 transition-colors mx-1">
              mysitebroker.com
            </a>
          </p>
        </div>
      </div>

      {/* Terms Panel */}
      <SlidePanel
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
        title="Terms of Service"
      >
        <TermsContent />
      </SlidePanel>

      {/* Privacy Panel */}
      <SlidePanel
        isOpen={isPrivacyOpen}
        onClose={() => setIsPrivacyOpen(false)}
        title="Privacy Policy"
      >
        <PrivacyContent />
      </SlidePanel>
    </footer>
  );
};

export default Footer;